"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";

import { DASHBOARD_ROUTES } from "@/dashboard/constants/routes";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { ALL_TENANTS_ID } from "@/dashboard/lib/auth/constants";

/**
 * Switch active tenant (client-side)
 * – POST /api/dashboard/tenant/select
 */
export function useSelectTenant(setActiveTenantId: (id: string) => void) {
  const qc = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  // checks if the path is /dashboard/hub/[customerId]
  const isCustomerDetail = pathname && /^\/dashboard\/hub\/[^/]+$/.test(pathname);

  return useMutation({
    mutationFn: (tenantId: string) =>
      fetchJson<{ success: true }>("/api/dashboard/tenant/select", {
        method: "POST",
        body: JSON.stringify({ tid: tenantId }),
      }).then(() => tenantId),
    onSuccess: (newTenantId: string) => {
      // ensure server-authoritative state is fetched afterwards
      void qc.invalidateQueries({ queryKey: ["me"] });

      // Clear all tenant-scoped caches so UI doesn't show stale data between tenants
      void qc.removeQueries({ queryKey: ["tenant"] });

      if (newTenantId !== ALL_TENANTS_ID) {
        if (isCustomerDetail) {
          // go to Hub root but tell it not to auto‑select
          router.replace(`${DASHBOARD_ROUTES.HUB}?skipAutoSelect=1`);
        } else {
          router.refresh();
        }
      }

      setActiveTenantId(newTenantId);
    },
  });
}
